import { PortfolioRepo } from "@services/portfolio.repo";
import { ClientCRMRepo } from "@services/clients/clientCRM.repo";
import { extractErrorMessage } from "@common/utils/errorUtils";

class NewPortfolioController {
  public clientNumber: number;
  public clientId: number;
  public portfolio: any;
  public portfolioId: number;
  public accountNumbers: any;
  public newPortfolio: boolean;
  public isLoading: boolean = false;

  /** @ngInject */
  constructor(
    private $mdDialog: angular.material.IDialogService,
    private $scope: angular.IScope,
    private portfolioRepo: PortfolioRepo,
    private clientCRMRepo: ClientCRMRepo,
    private $stateParams: angular.ui.IStateParamsService,
    private $q: angular.IQService,
    private notifyjs: any
  ) {}

  $onInit() {
    this.clientNumber = +this.$stateParams["id"];

    if (this.portfolioId) {
      this.portfolio = this.createEmptyPortfolio();
      this.isLoading = true;
      this.portfolioRepo.getPortfolio(this.portfolioId).then((val) => {
        this.portfolio = val.data;
        this.newPortfolio = false;
        this.loadAccounts();
      }).catch((error) => {
        this.isLoading = false;
        this.notifyjs("Failed to load portfolio details", "error");
        this.$mdDialog.cancel();
      });
    } else {
      this.portfolio = this.createEmptyPortfolio();
      this.newPortfolio = true;
      this.isLoading = true;
      this.loadAccounts();
    }

    const self = this;
    this.$scope.$watch(
      () => self.portfolio && self.portfolio.accounts,
      () => self.syncTradeAccountFromAccounts(),
      true
    );
  }

  /** Trade account is always the CDS linked from selected parent account(s); first match wins. */
  private syncTradeAccountFromAccounts(): void {
    if (!this.portfolio || !this.portfolio.accounts) {
      return;
    }
    for (const account of this.portfolio.accounts) {
      if (account.tradeAccountId) {
        this.portfolio.tradeAccount = {
          id: account.tradeAccountId,
          accountNumber: account.tradeAccountNumber,
          accountSourceName: "CDS",
          isActive: account.isActive !== false,
        };
        return;
      }
    }
    // No account is linked to a CDS — clear trade account to avoid orphaned CDS row (e.g. after removing the MCB account).
    this.portfolio.tradeAccount = null;
  }

  private createEmptyPortfolio(): any {
    return {
      portfolioId: 0,
      name: "",
      isActive: false,
      isStrategic: false,
      isManaged: false,
      isCharged: false,
      accounts: [],
      tradeAccount: null,
    };
  }

  loadAccounts() {
    this.getAccounts().then((accounts) => {
      const accountList = accounts as Array<any>;
      const unlinked = accountList.filter(
        (acc) => !acc.portfolioId && acc.accountSourceName !== "CDS"
      );
      if (
        !this.newPortfolio &&
        this.portfolio &&
        this.portfolio.accounts &&
        this.portfolio.accounts.length
      ) {
        const extras = this.portfolio.accounts.filter(
          (a: any) => !unlinked.some((u: any) => u.id === a.id)
        );
        this.accountNumbers = unlinked.concat(extras);
      } else {
        this.accountNumbers = unlinked;
      }
      this.syncTradeAccountFromAccounts();
    }).catch((error) => {
      this.notifyjs("Failed to load accounts", "error");
    }).finally(() => {
      this.isLoading = false;
    });
  }

  getAccounts() {
    if (!this.clientId) {
      console.error("Client ID is not available");
      return this.$q.resolve([]);
    }
    return this.clientCRMRepo
      .getPortfolioExternalAccounts(this.clientId)
      .then((result) => {
        const accounts = Array.isArray(result.data) ? result.data : [];
        return accounts.filter((a: any) => a.isActive !== false);
      });
  }

  removeAccount(account: any) {
    const accountToRemove = this.portfolio.accounts.findIndex(
      (acc: any) => acc.id === account.id
    );
    if (accountToRemove !== -1) {
      this.portfolio.accounts.splice(accountToRemove, 1);
    }
  }

  savePortfolio() {
    const hasExternalAccounts =
      this.portfolio.accounts && this.portfolio.accounts.length > 0;

    if (!hasExternalAccounts && this.portfolio.isActive) {
      this.notifyjs(
        "A portfolio without active external accounts must be inactive. Please deactivate the portfolio before saving."
      );
      return;
    }

    this.isLoading = true;
    this.portfolioRepo
      .savePortfolio(this.portfolio, this.clientNumber)
      .then((result) => {
        this.portfolio.portfolioId = result.data;
        this.$mdDialog.hide(this.portfolio);
      })
      .catch((error) => {
        const errorMessage = extractErrorMessage(error, "Could not save portfolio");
        this.notifyjs(errorMessage);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  cancel() {
    this.$mdDialog.hide();
  }

  public hasPortfolioFeesAttached(): boolean {
    if (!this.portfolio || !this.portfolio.portfolioId || this.portfolio.portfolioId === 0) {
      return false;
    }
    return this.portfolio.hasFees === true;
  }

  public isActiveWithoutFees(): boolean {
    return (
      this.portfolio &&
      this.portfolio.isActive === true &&
      !this.hasPortfolioFeesAttached() &&
      this.portfolio.portfolioId &&
      this.portfolio.portfolioId > 0
    );
  }

  public deactivatePortfolio(): void {
    if (this.portfolio) {
      this.portfolio.isActive = false;
    }
  }

  public hasInactiveAccounts(): boolean {
    const hasInactiveRegularAccounts =
      this.portfolio &&
      this.portfolio.accounts &&
      this.portfolio.accounts.length > 0 &&
      this.portfolio.accounts.some((acc: any) => acc.isActive === false);

    const hasInactiveTradeAccount =
      this.portfolio &&
      this.portfolio.tradeAccount &&
      this.portfolio.tradeAccount.isActive === false;

    return hasInactiveRegularAccounts || hasInactiveTradeAccount;
  }

  public isAccountInactive(account: any): boolean {
    return account && account.isActive === false;
  }

  /** Trade account on portfolio does not match any linked external account's CDS. */
  public hasOrphanTradeAccount(): boolean {
    if (!this.portfolio || !this.portfolio.tradeAccount || !this.portfolio.tradeAccount.id) {
      return false;
    }
    const tid = this.portfolio.tradeAccount.id;
    const linked = this.portfolio.accounts || [];
    return !linked.some((a: any) => a.tradeAccountId === tid);
  }

  /** Use the CDS from the first linked account that has a trade account. */
  public replaceOrphanTradeAccount(): void {
    this.syncTradeAccountFromAccounts();
  }
}

export const newPortfolioModal: angular.IComponentOptions = {
  template: require("./newportfolio.modal.html"),
  controller: NewPortfolioController,
  bindings: {
    portfolioId: "<",
    clientNumber: "<",
    clientId: "<",
  },
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/portfolio/modals/newportfolio.modal.ts